import { createApi } from "@reduxjs/toolkit/query/react";
import { privateBaseQuery } from "../base/private/privateBaseQuery";

/**
 * Заключения ревьюеров по попытке студента (тоггл «Честно / Не честно» + комментарий
 * и журнал «История заключения проктора(ов)»).
 *
 * Бэкенд-контракт: assignment-reviewer-reports.frontend.md
 * Базовый путь: /api/assignment-reviewer-reports (privateBaseQuery уже добавляет /api).
 */

/** Автор заключения (компактно — приходит только имя + id) */
export interface ReviewerReportOwner {
  id: number;
  firstname: string | null;
  lastname: string | null;
  patronymic: string | null;
}

/** Строка журнала заключений. status: true = Честно, false = Не честно */
export interface ReviewerReportEntity {
  id: number;
  status: boolean;
  comment: string;
  created_at: string; // ISO-8601 (UTC)
  owner: ReviewerReportOwner;
}

/** Агрегат по текущему мнению каждого ревьюера (последний вердикт = один голос) */
export interface ReviewerReportCounters {
  true: number;
  false: number;
  total: number;
}

/** Нормализованный ответ GET-истории */
export interface ReviewerReportsResult {
  entities: ReviewerReportEntity[];
  counters: ReviewerReportCounters;
}

/** Сырой envelope ответа GET */
interface ReviewerReportsResponseRaw {
  status?: string;
  entities?: ReviewerReportEntity[];
  counters?: ReviewerReportCounters;
}

/** Envelope ответа POST/PUT */
interface ReviewerReportEntityResponseRaw {
  status?: string;
  entity?: ReviewerReportEntity;
}

/** Тело POST/PUT. status: 1 = Честно, 0 = Не честно */
export interface SubmitReviewerReportPayload {
  attemptId: number;
  status: 0 | 1;
  comment: string;
}

export interface UpdateReviewerReportPayload extends SubmitReviewerReportPayload {
  id: number;
}

const EMPTY_COUNTERS: ReviewerReportCounters = { true: 0, false: 0, total: 0 };

export const reviewerReportsApi = createApi({
  reducerPath: "reviewerReportsApi",
  baseQuery: privateBaseQuery(),
  tagTypes: ["ReviewerReports"],
  endpoints: (builder) => ({
    /** История + счётчики по попытке */
    getReviewerReports: builder.query<ReviewerReportsResult, number>({
      query: (attemptId) => ({
        url: "/assignment-reviewer-reports",
        method: "GET",
        params: { assignment_attempt_id: attemptId },
      }),
      transformResponse: (response: ReviewerReportsResponseRaw): ReviewerReportsResult => ({
        entities: Array.isArray(response?.entities) ? response.entities : [],
        counters: response?.counters ?? EMPTY_COUNTERS,
      }),
      providesTags: (_result, _error, attemptId) => [
        { type: "ReviewerReports", id: attemptId },
      ],
    }),

    /** Вынести / сменить вердикт (или поправить коммент при том же статусе) */
    submitReviewerReport: builder.mutation<
      ReviewerReportEntity | null,
      SubmitReviewerReportPayload
    >({
      query: ({ attemptId, status, comment }) => ({
        url: "/assignment-reviewer-reports",
        method: "POST",
        data: { assignment_attempt_id: attemptId, status, comment },
      }),
      transformResponse: (r: ReviewerReportEntityResponseRaw) => r?.entity ?? null,
      invalidatesTags: (_result, _error, { attemptId }) => [
        { type: "ReviewerReports", id: attemptId },
      ],
    }),

    /** Правка своего заключения (только автор) */
    updateReviewerReport: builder.mutation<
      ReviewerReportEntity | null,
      UpdateReviewerReportPayload
    >({
      query: ({ id, attemptId, status, comment }) => ({
        url: `/assignment-reviewer-reports/${id}`,
        method: "PUT",
        data: { assignment_attempt_id: attemptId, status, comment },
      }),
      transformResponse: (r: ReviewerReportEntityResponseRaw) => r?.entity ?? null,
      invalidatesTags: (_result, _error, { attemptId }) => [
        { type: "ReviewerReports", id: attemptId },
      ],
    }),

    /** Удаление своего заключения (soft delete, только автор) */
    deleteReviewerReport: builder.mutation<void, { id: number; attemptId: number }>({
      query: ({ id }) => ({
        url: `/assignment-reviewer-reports/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { attemptId }) => [
        { type: "ReviewerReports", id: attemptId },
      ],
    }),
  }),
});

export const {
  useGetReviewerReportsQuery,
  useSubmitReviewerReportMutation,
  useUpdateReviewerReportMutation,
  useDeleteReviewerReportMutation,
} = reviewerReportsApi;
